/**
 * @description 项目级主题运行时 token，供 JS 逻辑与布局计算复用
 * @reason 保持 JS 侧断点、容器宽、头部高度与 styles/_variables.scss 的设计基线同步
 */
export const THEME_BREAKPOINTS = Object.freeze({
  xs: '0px',
  sm: '576px',
  md: '768px',
  lg: '1025px',
  xl: '1280px',
  xxl: '1440px',
  xxxl: '1920px',
})

/**
 * @description 项目级布局 token，和 SCSS 变量保持同源语义
 */
export const THEME_LAYOUT = Object.freeze({
  containerWidth: 1400,
  containerWidthCompactDesktop: 1248,
  containerWidthTablet: 736,
  containerWidthPhone: 343,
  pagePadding: 16,
  gutterDesktop: 16,
  gutterMobile: 12,
  headerHeight: 48,
  headerHeightMobile: 48,
})

/**
 * @description 运行时复用的颜色 token，供 JS 配置、Canvas 绘制和内联样式使用
 */
export const THEME_COLORS = Object.freeze({
  primary: '#fb5100',
  primaryHover: '#fc762e',
  danger: '#F53F3F',
  info: '#3852d6',
  warning: '#ff7716',
  success: '#0ca300',
  textPrimary: '#202427',
  textSecondary: '#4f565f',
  textTertiary: '#808893',
  textCaption: '#a1a1a6',
  textStrong: '#1d2129',
  textBodyStrong: '#333333',
  textSecondaryWeak: '#666666',
  bgPage: '#f7f7f7',
  bgSoft: '#f5f5f5',
  bgMuted: '#f5f6f8',
  bgPanelMuted: '#f7f8fa',
  bgSurface: '#ffffff',
  borderLight: '#e5e6eb',
  borderMuted: '#e0e0e0',
})

/**
 * @description 运行时复用的 CSS 变量出口，避免模板和 JS 逻辑散落硬编码色值
 */
export const THEME_CSS_VARS = Object.freeze({
  primary: 'var(--ip-color-primary)',
  primaryHover: 'var(--ip-color-primary-hover)',
  textPrimary: 'var(--ip-text-primary)',
  textSecondary: 'var(--ip-text-secondary)',
  textTertiary: 'var(--ip-text-tertiary)',
  textCaption: 'var(--ip-text-caption)',
  textStrong: 'var(--ip-text-strong)',
  textBodyStrong: 'var(--ip-font-color-secondary, #333333)',
  textSecondaryWeak: 'var(--ip-font-color-caption, #666666)',
  bgPage: 'var(--ip-bg-page)',
  bgSurface: 'var(--ip-bg-surface)',
  bgSoft: 'var(--ip-bg-soft)',
  bgMuted: 'var(--ip-bg-muted)',
  bgPanelMuted: 'var(--ip-bg-panel-muted)',
  borderLight: 'var(--ip-border-light)',
  borderMuted: 'var(--ip-border-muted)',
  borderDefault: 'var(--ip-border-default)',
})

/**
 * @description 获取断点像素值
 * @param {keyof typeof THEME_BREAKPOINTS} breakpoint - 断点名称
 * @returns {number} 像素值
 */
export const getBreakpointPx = (breakpoint) => {
  return Number.parseInt(THEME_BREAKPOINTS[breakpoint], 10)
}

/**
 * @description 判断当前视口是否为移动端
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否移动端
 */
export const isMobileViewport = (width) => {
  return Number(width) < getBreakpointPx('lg')
}

/**
 * @description 判断当前视口是否为手机尺寸
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否为手机
 */
export const isPhoneViewport = (width) => {
  return Number(width) < getBreakpointPx('md')
}

/**
 * @description 判断当前视口是否为平板尺寸
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否为平板
 */
export const isTabletViewport = (width) => {
  const viewportWidth = Number(width)
  return viewportWidth >= getBreakpointPx('md') && viewportWidth < getBreakpointPx('lg')
}

/**
 * @description 判断当前视口是否进入桌面交互区间
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否为桌面
 */
export const isDesktopViewport = (width) => {
  return Number(width) >= getBreakpointPx('lg')
}

/**
 * @description 判断当前视口是否处于紧凑桌面区间
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否为紧凑桌面
 */
export const isCompactDesktopViewport = (width) => {
  const viewportWidth = Number(width)
  return viewportWidth >= getBreakpointPx('lg') && viewportWidth < getBreakpointPx('xl')
}

/**
 * @description 判断当前视口是否进入标准 PC 栅格区间
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否为标准 PC
 */
export const isWideDesktopViewport = (width) => {
  return Number(width) >= getBreakpointPx('xl')
}

/**
 * @description 判断当前视口是否采用移动/平板头部形态
 * @param {number} width - 视口宽度
 * @returns {boolean} 是否采用紧凑头部
 */
export const isCompactHeaderViewport = (width) => {
  return Number(width) < getBreakpointPx('lg')
}

/**
 * @description 根据视口宽度获取基础头部高度
 * @param {number} width - 视口宽度
 * @returns {number} 头部高度
 */
export const getBaseHeaderHeightByWidth = (width) => {
  return isCompactHeaderViewport(width)
    ? THEME_LAYOUT.headerHeightMobile
    : THEME_LAYOUT.headerHeight
}
